import {
  type OverrideConflictContent,
  OverrideDialog,
  getOverrideConflictContent,
} from "@/components/OverrideDialog";
import { toast } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import type { AvailableActionsResponse_Action } from "@/network/useAvailableActions";
import {
  TAKE_ENCOUNTER_ERROR_TOAST_ID,
  getTakeEncounterErrorMessage,
  useTakeEncounter,
} from "@/network/useTakeEncounter";
import { useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";

interface TakePatientButtonProps {
  takeAction: AvailableActionsResponse_Action | undefined;
}

export const TakePatientButton = ({ takeAction }: TakePatientButtonProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const searchParams = useSearch({ from: "/patient/$patientId" });
  const [overrideContent, setOverrideContent] =
    useState<OverrideConflictContent | null>(null);

  const encounterId = searchParams.encounterId as string;
  const takeEncounter = useTakeEncounter({
    patientId,
    encounterId,
    showErrorToast: false,
  });

  const handleClickTake = async (overrideBlock = false) => {
    try {
      await takeEncounter.mutateAsync(overrideBlock);
      setOverrideContent(null);
    } catch (error: unknown) {
      // Show the override dialog for overridable blocks; otherwise toast the
      // error. The two are mutually exclusive so the toast never doubles up
      // with the dialog.
      const content = getOverrideConflictContent(error);
      if (content) {
        setOverrideContent(content);
      } else {
        toast.error(getTakeEncounterErrorMessage(error), {
          id: TAKE_ENCOUNTER_ERROR_TOAST_ID,
        });
      }
    }
  };

  if (!takeAction) {
    return null;
  }

  return (
    <>
      <Button
        className="leading-tight py-0 h-6"
        size="xs"
        onClick={() => handleClickTake(false)}
        disabled={!takeAction.allowed}
        isLoading={takeEncounter.isPending}
      >
        Take
      </Button>
      <OverrideDialog
        isOpen={overrideContent !== null}
        onOpenChange={(open) => {
          if (!open) {
            setOverrideContent(null);
          }
        }}
        onOverride={() => handleClickTake(true)}
        isLoading={takeEncounter.isPending}
        title={overrideContent?.title ?? ""}
        messages={overrideContent?.messages}
      />
    </>
  );
};
